import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Marker, Polygon, Polyline } from '@react-google-maps/api';
import { useQueries, useQuery as useTanStackQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FilterSelect,
  FormControlLabel,
  Map,
  Stack,
  Typography,
} from '../../../ui/core';
import { cardHeaderSx, pillButtonSx } from '../../../ui/styles';
import { StationIcon } from '../../../ui/icons';
import {
  useCandidateEstimate,
  useCandidateSweep,
  useBatchCreateStations,
  useCities,
  useDebouncedValue,
  useQuery,
  useRecordRejections,
} from '../../../hooks';
import laneMapApi from '../../../lib/api/LaneMapApi';
import {
  buildCityPairs,
  geoJsonToPath,
  geoJsonToPolygonPaths,
  laneColorFor,
} from '../../Location/laneGeometry';
import {
  DEFAULT_SEARCH_PARAMS,
  requestParamsKey,
  toRequestParams,
} from '../../../lib/stationCandidates/searchParams';
import AreaSelect from './AreaSelect';
import SearchParamsPanel from './SearchParamsPanel';
import CandidateReviewList, {
  APPROVED,
  REJECTED,
  canCreate,
  canReject,
  rating as ratingLabel,
  submittableFor,
} from './CandidateReviewList';

/**
 * The station builder: pick a city, look at its lanes, place a rectangle over the
 * area, price the sweep before spending anything, run it, review, create.
 *
 * Five decisions here are load-bearing rather than stylistic.
 *
 * 1. THE SELECTED CITY IS NEVER SENT TO A CANDIDATE ENDPOINT. `CandidateSearchDto`
 *    has no `cityId` field, and `ValidationPipe({ whitelist: true })` would strip
 *    one silently rather than 400 — so adding one would look like it worked while
 *    changing nothing. The backend's corridor filter measures against the lanes
 *    this page SENDS (`laneIds`, see `LANE_SCOPE`), and each candidate's
 *    `cityName`/`cityState`/`cityMongoId` are derived from that candidate's own
 *    coordinates. The city selected here therefore does exactly two things: it
 *    frames the map and it scopes the lane fetch. Nothing else — the lane
 *    selection made from that fetch is what reaches the sweep.
 *
 * 2. THE CITY AND THE DRAWN BBOX LIVE IN THE URL. Same reasoning phase 3 wrote
 *    down for `StationsTable`'s selection: the writers of this state are not all
 *    this component (`AreaSelect` writes the bbox, the city pill writes the city),
 *    and parent-local state severs a sibling writer silently. It also makes an
 *    expensive-to-reproduce screen shareable — "this exact box in this city" is
 *    the thing an operator wants to send to someone else before spending money on
 *    it. The search params are deliberately NOT in the URL: they are a dozen
 *    fields, several of them arrays, and `toRequestParams` already guarantees the
 *    estimate and the sweep are quoted from one normalisation.
 *
 * 3. ONLY TWO LANE LAYERS: POLYLINES, AND THE STATION CORRIDOR BAND. The lane
 *    page's other bands (shared door, along-route, far-city exclusion, lane match,
 *    city radius) answer "where do the lane limits bite?", which is a different
 *    question from "where should a station go", and each one drags lane-limits
 *    state onto this page. The corridor band earns its place because it is
 *    literally the region in which a candidate survives the sweep's own corridor
 *    filter — so it is drawn at THE SWEEP'S `corridorRadiusMeters`, off the params
 *    panel, not at the eligibility limits' `stationToPolylineMeters`. Drawing the
 *    lane page's radius here would show a band the sweep is not using. Since the
 *    sweep now measures against the SELECTED lanes, the band and the filter are
 *    computed from one set and cannot describe different lanes — see `LANE_SCOPE`.
 *
 * 4. SUBMISSION IS CHUNKED AND ITS OUTCOME IS AGGREGATED, NOT ASSUMED.
 *    `POST /admin/stations/batch` takes at most 100 rows (`@ArrayMaxSize(100)`) and
 *    returns 201 EVEN WHEN ROWS FAILED; `POST /candidates/rejections` takes at most
 *    200. See `SUBMIT_REPORTING` below for what that forces.
 *
 * 5. A BATCH ITEM IS A PICK, NOT A TRANSFORM. See `toBatchItem`.
 *
 * 6. EXISTING STATIONS PIN TO THE VIEWPORT, NOT TO THE SELECTED CITY. See
 *    `EXISTING_STATION_PINS`.
 *
 * 7. CANDIDATE PINS ARE A SECOND, DELIBERATELY DIFFERENT-LOOKING LAYER. See
 *    `CANDIDATE_PINS`.
 *
 * 8. A SWEEP ALSO BELONGS TO THE PARAMS THAT PRODUCED IT — but drifting params
 *    BLOCK CREATION rather than discarding the results. See `PARAM_DRIFT`.
 *
 * 9. THE LANE CHECKBOXES ARE PART OF THE REQUEST, NOT A LEGEND. See `LANE_SCOPE`.
 */

/** Geographic centre of the contiguous US — a sane "no city picked yet" frame. */
export const FALLBACK_CENTER = { lat: 39.8283, lng: -98.5795 };
export const FALLBACK_ZOOM = 4;
/** Close enough to see a metro's lanes and frame a plausible sweep box. */
export const CITY_ZOOM = 9;
/**
 * Matched on `cityName`, not on an id: ids differ per environment, so a
 * hardcoded one would resolve on staging and silently fall through to the
 * US-wide view in production. Missing from the list is a normal outcome (an
 * unusual permission set, or Austin marked non-operational) and simply leaves
 * no city selected.
 */
export const DEFAULT_CITY_NAME = 'Austin';

/**
 * EXISTING_STATION_PINS — what is already there, so an operator can see it
 * before siting something new.
 *
 * FOUR THINGS ARE LOAD BEARING.
 *
 * 1. SCOPED BY VIEWPORT, NEVER BY THE SELECTED CITY. The backend's
 *    `nearExistingStation` drop reason is pure geometry — `stationsInBounds` has
 *    no city predicate at all, and station `city`/`state` are a denormalised
 *    label. City-scoped pins would therefore under-show: a candidate could be
 *    dropped because of a station one town over that never appeared on the map,
 *    which is the most confusing possible version of that drop. Whatever is on
 *    screen is what is pinned.
 *
 * 2. KEYED ON A SERIALISED VIEWPORT STRING, NOT ON THE BOUNDS OBJECT.
 *    `getBounds()` hands back a fresh object every time and `idle` fires more
 *    than once per settled gesture (overlay churn re-emits it). A new object as
 *    the query key would refetch on every one of those, and it would also
 *    defeat `useDebouncedValue`, whose effect deps are reference-compared — a
 *    fresh object each idle restarts the timer instead of settling it. A string
 *    makes an unchanged viewport a no-op at the state layer, and the debounce
 *    then coalesces a burst of genuinely different viewports into one fetch.
 *
 * 3. OPERATIONAL STATIONS ARE PINNED BY DEFAULT; NON-OPERATIONAL ONES ARE AN
 *    OPT-IN SECOND LAYER. `/admin/lane-map/stations` returns every station in
 *    the envelope "regardless of operational status" — there is no request
 *    parameter to change here and none is sent; the split is the client-side
 *    `isOperational` filter below.
 *
 *    Default off, because a switched-off station is not a reason to avoid
 *    siting here: it does not appear in rider-facing quote selection
 *    (`getStationsAlongRoute` gates on `is_operational = true`) and it is not
 *    what `nearExistingStation` is warning about. Turned on for the OTHER job
 *    this page is asked to do — managing stations that exist but are invisible
 *    everywhere else, including the ones the candidate builder used to create
 *    switched-off. So they render in a distinct hollow, faded style
 *    (`NON_OPERATIONAL_STATION_ICON`) and say so in the tooltip: an operator
 *    must never read one as coverage that riders can actually book.
 *
 * 4. NOT LaneMap's `stationIcon`/`stationTooltip`. Those encode the three
 *    `STATION_PAIR_VERDICTS` (lane-eligibility outcomes) which have no meaning
 *    on this page, so they are not generalised — this page gets a plain dot.
 *
 * THE FIELD-NAME TRAP. This endpoint returns `isOperational` and FLAT
 * `latitude`/`longitude` scalars. `GET /admin/stations` — a different endpoint,
 * behind `useStations` — returns `operational` and GeoJSON
 * `coord: { coordinates: [lon, lat] }`. Reaching for the wrong pair pins every
 * marker at `undefined` or swaps every coordinate, and neither failure is loud.
 * The full row is exactly: `id`, `mongoId`, `name`, `address`, `city`, `state`,
 * `latitude`, `longitude`, `partnerType`, `isOperational`. THERE IS NO
 * `routeType` ON IT — see `existingStationTooltip`.
 */

/** How long a pan/zoom must settle before the viewport station fetch fires. */
export const VIEWPORT_DEBOUNCE_MS = 400;

/** A circle of radius 5.5 about the origin. A path literal — see below. */
export const EXISTING_STATION_PIN_PATH =
  'M 0,0 m -5.5,0 a 5.5,5.5 0 1,0 11,0 a 5.5,5.5 0 1,0 -11,0';

/**
 * A plain dot. Deliberately not one of LaneMap's verdict styles: an existing
 * station is a fact about the world, not an outcome of a filter, so it gets one
 * appearance and no legend.
 *
 * WHY A PATH LITERAL AND NOT `SymbolPath.CIRCLE`. This previously read
 * `window.google?.maps?.SymbolPath?.CIRCLE`, and shipped every station as
 * Google's default red teardrop. The optional chain was added so the module
 * would not throw under test, where `window.google` is absent — but it also
 * turns "Maps has not loaded yet" into `path: undefined`, which Google silently
 * treats as "no icon given" and falls back to its default pin. The icon was
 * then memoised on `[showExistingStations]`, so it was computed once on a render
 * that happens BEFORE `useJsApiLoader` resolves and never recomputed after.
 * Frozen wrong, with no error anywhere.
 *
 * This is the same failure mode as the `DrawingManager` removal earlier on this
 * branch: reading a Maps global defensively converts a load-order problem into a
 * silently degraded UI. A path literal has no `window.google` dependency at all,
 * so there is no load order to get wrong, and — as with `CANDIDATE_ICONS` — it
 * is a frozen constant with stable identity, so `<Marker>` does not rebuild
 * every pin on the map on every keystroke in the params panel.
 */
export const EXISTING_STATION_ICON = Object.freeze({
  path: EXISTING_STATION_PIN_PATH,
  fillColor: '#37474F',
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 1.5,
  scale: 1,
});

/**
 * The non-operational variant: same dot, hollow and faded. Mirrors LaneMap's
 * treatment of the same fact (`stationIcon`, where non-operational outranks
 * every eligibility verdict) so the two maps do not disagree about what a
 * switched-off station looks like.
 *
 * DELIBERATELY NOT A COLOUR SWAP. Candidates already use colour to carry the
 * accept/reject decision (`CANDIDATE_ICONS`), so a third colour would read as a
 * fourth decision. Fill is what separates these two: solid means live, hollow
 * means switched off, at the same size and in the same hue family.
 *
 * A frozen constant with stable identity, for the same reason
 * `EXISTING_STATION_ICON` is one — see the note above it.
 */
export const NON_OPERATIONAL_STATION_ICON = Object.freeze({
  path: EXISTING_STATION_PIN_PATH,
  fillColor: '#FFFFFF',
  fillOpacity: 0.35,
  strokeColor: '#37474F',
  strokeOpacity: 0.55,
  strokeWeight: 1.5,
  scale: 1,
});

/**
 * Takes the station rather than closing over the toggle: which of the two
 * frozen icons a pin gets is a property of that station, and both are stable
 * references, so `<Marker>` still sees an unchanged `icon` across renders.
 */
export const existingStationIcon = (station) =>
  station?.isOperational === false ? NON_OPERATIONAL_STATION_ICON : EXISTING_STATION_ICON;

/**
 * Name and address, which is what identifies a station to an operator deciding
 * whether the thing on their screen is already covered.
 *
 * BOTH FIELDS ARE REAL AND NOTHING ELSE IS ADDED. LaneMap's tooltip opens
 * `${station.name} (${station.routeType})`, and `routeType` is not a column
 * `stationsInBounds` selects — nor does it appear anywhere in the station DAO —
 * so that header renders "(undefined)" for viewport stations in production and
 * only looks right in tests because the fixtures invent the field. Copying the
 * shape would have imported the bug.
 *
 * Joined through a filter rather than interpolated: `address` is the raw
 * `station.address` column and is nullable, and a template literal renders a
 * dangling "undefined" line under the name for one.
 *
 * The one addition is a "Not operational" line, and only for a station that is
 * one. The icon already differs, but hollow-vs-solid is a comparison — it only
 * reads as a distinction when both are on screen, and a viewport can easily
 * hold nothing but switched-off stations. The line makes a single pin
 * self-explanatory. Same wording as LaneMap's `stationTooltip`, and appended
 * through the same filter so an absent `isOperational` (a shape this endpoint
 * does not produce, but callers may) adds no line rather than claiming one.
 */
export const existingStationTooltip = (station) =>
  [
    station.name,
    station.address,
    station.isOperational === false ? 'Not operational — not bookable by riders' : null,
  ]
    .filter(Boolean)
    .join('\n');

/**
 * CANDIDATE_PINS — what the sweep proposed, and what the operator has said about
 * it, on the map rather than only in the list.
 *
 * FIVE THINGS ARE LOAD BEARING.
 *
 * 1. A DIFFERENT SHAPE FROM AN EXISTING STATION, NOT JUST A DIFFERENT COLOUR.
 *    "Already there" and "proposed" are different kinds of thing and the two
 *    layers sit on top of each other by construction — a candidate is sited near
 *    lanes, which is where the stations are. Colour alone would make the map
 *    unreadable for a colour-blind operator and ambiguous for everyone else, so
 *    candidates are DIAMONDS and existing stations stay plain CIRCLES. Colour
 *    then carries only the decision, which is the one thing that changes.
 *
 * 2. THE SHAPE IS AN SVG PATH, NOT A `SymbolPath` ENUM. `SymbolPath` has no
 *    square or diamond member (it is circle plus four arrows), and reading it
 *    also means reading `window.google`, which does not exist under test. A path
 *    literal is a plain string, so these icons are module constants with STABLE
 *    IDENTITY — `<Marker>` re-creates its icon whenever the prop's value changes,
 *    and a fresh object literal per render would rebuild every pin on the map on
 *    every keystroke in the params panel.
 *
 * 3. THE FLAT `latitude`/`longitude` ON THE CANDIDATE, exactly as with stations —
 *    but the two shapes are NOT interchangeable and must not be blurred. A
 *    `/admin/lane-map/stations` row carries `isOperational` and a station `id`; a
 *    `GeneratedCandidate` carries neither and is keyed on `placeId`. The fields
 *    read here are all off `GeneratedCandidate extends PlaceCandidate`
 *    (`apps/admin/src/station-candidates.service.ts` +
 *    `station-candidates.filters.ts`): `placeId`, `name`, `address`, `latitude`,
 *    `longitude`, `primaryType` (`string | null`), `rating` (`number | null`),
 *    `userRatingCount` (`number | null`), `hoursUnknown` (`boolean`). Nothing
 *    else, and nothing invented.
 *
 * 4. ONE SOURCE OF DECISION TRUTH. The pins read the SAME `decisions` map the
 *    review list is controlled by and write through the SAME `onDecide`, so
 *    approving in either place is immediately visible in the other. A second
 *    map-local copy would drift the moment a submit pruned settled rows.
 *
 * 5. NOTHING TO CLEAR ON DISCARD. Because the pins are derived from `candidates`
 *    and `decisions`, the existing "a sweep belongs to the area that produced it"
 *    reset and the post-submit prune clear the pins for free. There is no third
 *    place to remember to empty.
 */

/** A diamond, ~9px from centre. See `CANDIDATE_PINS` item 2 for why it is a path. */
export const CANDIDATE_PIN_PATH = 'M 0,-9 9,0 0,9 -9,0 z';

/**
 * The third state, which `decisions` represents by ABSENCE — there is no wire
 * value for "undecided" and inventing one in `decisions` would make it a
 * submittable decision. It is only ever an icon key.
 */
export const CANDIDATE_UNDECIDED = 'undecided';

const candidatePinStyle = (fillColor, fillOpacity) =>
  Object.freeze({
    path: CANDIDATE_PIN_PATH,
    fillColor,
    fillOpacity,
    strokeColor: '#FFFFFF',
    strokeWeight: 1.5,
    scale: 1,
  });

/**
 * Frozen module constants, one per decision state — see item 2 on identity.
 *
 * A rejected pin is drawn at a low fill opacity as well as in red: an operator
 * scanning the map is looking for what is left to do, and a veto should recede
 * rather than compete with the undecided pins for attention.
 */
export const CANDIDATE_ICONS = Object.freeze({
  [CANDIDATE_UNDECIDED]: candidatePinStyle('#FB8C00', 0.95),
  [APPROVED]: candidatePinStyle('#2E7D32', 0.95),
  [REJECTED]: candidatePinStyle('#C62828', 0.35),
});

export const candidateIcon = (decision) =>
  CANDIDATE_ICONS[decision] ?? CANDIDATE_ICONS[CANDIDATE_UNDECIDED];

const DECISION_LINE = {
  [APPROVED]: 'Approved — will be created',
  [REJECTED]: 'Rejected — will be recorded so the next sweep skips it',
};

/**
 * Enough to judge the place without going back to the list: what it is, how well
 * reviewed, whether its hours are unknown, and what has already been said.
 *
 * `name`/`address` fall back to the same placeholders the review row uses rather
 * than being dropped, because a blank one is REACHABLE (`toPlaceCandidate` sets
 * `name: displayName.text ?? ''`) and a pin with an empty tooltip looks like a
 * rendering fault instead of a place Places under-reported. `rating` is formatted
 * through the review list's own helper: it is `number | null` and null means
 * UNKNOWN, not zero.
 */
export const candidateTooltip = (candidate, decision = null) =>
  [
    candidate.name || '(unnamed place)',
    candidate.address || '(no address)',
    [
      candidate.primaryType || 'no primary type',
      ratingLabel(candidate.rating, candidate.userRatingCount),
    ].join(' · '),
    candidate.hoursUnknown ? 'Hours unknown — Places did not report opening hours' : null,
    DECISION_LINE[decision] ?? null,
  ]
    .filter(Boolean)
    .join('\n');

/**
 * One click on a pin advances undecided → approved → rejected → undecided.
 *
 * A map pin has one gesture and there are three states, so it cycles rather than
 * offering two controls. IT SKIPS WHAT THE DTOs WOULD REFUSE, for the same reason
 * the list disables those buttons: `StationBatchItemDto` requires a non-blank
 * `name` AND `address`, `CandidateRejectionDto` requires `name`, and one blank
 * field 400s a whole chunk. So a candidate with no address can only be rejected,
 * and one with no name cannot be decided at all — clicking it is a no-op rather
 * than a decision that cannot be honoured.
 */
export const nextCandidateDecision = (decision, candidate) => {
  const cycle = [null, APPROVED, REJECTED];
  const from = cycle.indexOf(decision ?? null);
  for (let step = 1; step <= cycle.length; step += 1) {
    const next = cycle[(from + step) % cycle.length];
    if (next === null || submittableFor(next, candidate)) return next;
  }
  return null;
};

/** `@ArrayMaxSize(100)` on `StationBatchCreateDto.stations`. */
export const BATCH_CHUNK_SIZE = 100;
/** `@ArrayMaxSize(200)` on `CandidateRejectionsDto.rejections`. */
export const REJECTION_CHUNK_SIZE = 200;

export const chunk = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

/**
 * `GeneratedCandidate` -> `StationBatchItemDto`. A PICK OF NINE FIELDS AND
 * NOTHING ELSE.
 *
 * The candidate already carries flat `latitude`/`longitude` scalars,
 * `partnerType: 'regular'` (in `PARTNER_TYPES`, which `@IsIn` checks) and
 * `operational: false`, because the service sets them for exactly this purpose.
 * So there is no coordinate remapping to do, and none should be invented.
 *
 * TWO TRAPS THIS FUNCTION EXISTS TO AVOID:
 *
 * `cityMongoId`, NEVER `cityId`. A candidate carries BOTH — `cityId` is the
 * Postgres uuid, `cityMongoId` is the 24-hex Mongo id — and
 * `StationBatchItemDto.cityMongoId` is validated with `@IsMongoId()`. Reaching
 * for the more obvious-looking `cityId` 400s every single row.
 *
 * NOT `StationsApi`'s `toRestPayload`/`toCompatShape`. Those exist to keep
 * GraphQL-era consumers working and are asymmetric on purpose (GeoJSON `coord`
 * in, scalars out); a candidate pushed through them comes out coordinate-swapped.
 */
export const toBatchItem = (candidate) => ({
  name: candidate.name,
  address: candidate.address,
  partnerType: candidate.partnerType,
  cityMongoId: candidate.cityMongoId,
  placeId: candidate.placeId,
  operational: candidate.operational,
  latitude: candidate.latitude,
  longitude: candidate.longitude,
  // `station.place_type` — the Google primary type, free text and nullable.
  //
  // Omitting it left the column NULL on every row the sweep ever created, which
  // is the same value the ~975 pre-existing rows carry: the point of the column
  // is that a hotel sited for 04:00 coverage can LATER be told apart from a cafe
  // by an hour-aware query, and a null-everywhere column cannot do that. Nothing
  // backfills it, so a row created without it is unrecoverable short of a
  // re-scrape.
  //
  // Deliberately NOT `partnerType`: place class and commercial relationship are
  // separate concerns, and the backend kept them apart on purpose.
  placeType: candidate.primaryType,
});

/**
 * `GeneratedCandidate` -> `CandidateRejectionDto`.
 *
 * All four of these are REQUIRED on that DTO (`@IsNotEmpty()` on both strings,
 * `@IsNumber()` on both coordinates) — a rejection is not just a place id.
 * `reason` is the only optional field and nothing here collects one, so it is
 * omitted rather than filled with an invented string.
 *
 * Recording rejections is what stops the next sweep over the same area
 * re-proposing the same places: they come back as `dropped.previouslyRejected`.
 * Skipping them is not a harmless omission, it is a guarantee of repeat work.
 */
export const toRejection = (candidate) => ({
  placeId: candidate.placeId,
  name: candidate.name,
  latitude: candidate.latitude,
  longitude: candidate.longitude,
});

/**
 * SUBMIT_REPORTING — how a chunked submit reports what actually happened.
 *
 * `POST /batch` is 201 on partial failure, so "did the request resolve" and "did
 * the rows land" are different questions and only the second one matters to an
 * operator. Three cases have to stay distinguishable:
 *
 *   - a row that failed inside a 201 (`results[i].status === 'failed'`),
 *   - a whole chunk whose request failed (nothing in it landed),
 *   - everything else, which landed.
 *
 * So counts are SUMMED across chunks rather than taken from the last one, and a
 * failed request contributes `chunk.length` to `failedCount` — those rows did
 * not land, and reporting them as neither created nor failed would lose them.
 * `results[].index` is per-request, so it is offset by the chunk's start to name
 * a row the operator can find in the list they were just looking at.
 *
 * Rejections are all-or-nothing per chunk: the controller loops and throws on the
 * first failure, returning only `{ recorded }`, so there is no per-row detail to
 * report and a failed chunk means its whole slice was not recorded.
 *
 * THE ROW INDEPENDENCE THE CONTROLLER PROVIDES DOES NOT EXTEND TO DTO VALIDATION,
 * which is why `blocked` exists. `@ValidateNested({ each: true })` runs in
 * `ValidationPipe`, ahead of the controller's per-row `try/catch`, so a single row
 * with a blank `name` or `address` 400s its entire chunk — up to 100 stations
 * failing together on one row's fault, unrecoverably (every retry sends the same
 * offender). Such rows are therefore filtered out BEFORE the payload is built and
 * reported as their own category: not created, not "failed", and named.
 */
const summarise = ({ createdCount, failedCount, failures, recordedCount, rejectionErrors, blocked }) => {
  const parts = [];
  parts.push(`${createdCount} station${createdCount === 1 ? '' : 's'} created`);
  if (failedCount > 0) {
    parts.push(`${failedCount} failed`);
  }
  parts.push(`${recordedCount} rejection${recordedCount === 1 ? '' : 's'} recorded`);
  if (blocked.length > 0) {
    parts.push(`${blocked.length} skipped as missing name or address`);
  }
  if (rejectionErrors.length > 0) {
    parts.push(`${rejectionErrors.length} rejection batch${rejectionErrors.length === 1 ? '' : 'es'} failed`);
  }
  if (failures.length > 0) {
    parts.push('see the rows below');
  }
  // Built as one JS string on purpose: MUI/JSX interpolation splits
  // `{n} text` into separate children, and a text joiner then sees a double
  // space where a test asserts a number adjacent to prose.
  return `${parts.join(' · ')}.`;
};

const StationBuilder = () => {
  const history = useHistory();
  const { cityId, swLng, swLat, neLng, neLat } = useQuery();

  const [params, setParams] = useState(DEFAULT_SEARCH_PARAMS);
  const [candidates, setCandidates] = useState([]);
  // How many candidates the sweep RETURNED. `candidates` is pruned as rows settle
  // (see the submit handler), and `stats` is not — so the stats block's
  // "Unique places = candidates kept + these" identity has to be rendered against
  // this immutable count, never against the live list length.
  const [sweptCandidateCount, setSweptCandidateCount] = useState(0);
  // The serialised REQUEST params the listed candidates were swept with, or null
  // before any sweep. See `PARAM_DRIFT`.
  const [sweptParamsKey, setSweptParamsKey] = useState(null);
  // How many lanes the listed sweep was ASKED to measure against. Compared with
  // the backend's `stats.corridorRouteCount` in the stats block; see `onGenerate`.
  const [sweptLaneCount, setSweptLaneCount] = useState(null);
  const [stats, setStats] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [sweepError, setSweepError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hiddenPairKeys, setHiddenPairKeys] = useState([]);
  const [showCorridorBand, setShowCorridorBand] = useState(true);
  const [showExistingStations, setShowExistingStations] = useState(true);
  // Default off; see `EXISTING_STATION_PINS` item 3. Independent of the fetch —
  // the endpoint returns both kinds either way — so toggling it never refetches.
  const [showNonOperationalStations, setShowNonOperationalStations] = useState(false);
  const [showCandidatePins, setShowCandidatePins] = useState(true);
  const [map, setMap] = useState(null);
  // The current viewport, serialised. See `EXISTING_STATION_PINS` item 2 for why
  // this is a string in state rather than the bounds object.
  const [viewportKey, setViewportKey] = useState(null);

  // Cities matching the lane page's own picker: operational only (a retired
  // market has no lanes to draw and no reason to site a station in) and sorted,
  // so the same pill in two places lists the same things in the same order.
  const { cities } = useCities({ operationalOnly: true, sorted: true });
  const selectedCity = useMemo(
    () => (cityId ? cities.find((city) => city.id === cityId) || null : null),
    [cityId, cities],
  );

  // The four scalars arrive from the URL as strings; everything downstream —
  // `StationCandidatesApi`'s bbox guard, `<Rectangle>`'s bounds literal — needs
  // finite numbers, and a partial bbox is no bbox at all.
  const bbox = useMemo(() => {
    const parsed = {
      swLng: Number(swLng),
      swLat: Number(swLat),
      neLng: Number(neLng),
      neLat: Number(neLat),
    };
    const present = [swLng, swLat, neLng, neLat].every((value) => value !== undefined && value !== '');
    if (!present || !Object.values(parsed).every((value) => Number.isFinite(value))) return null;
    return parsed;
  }, [swLng, swLat, neLng, neLat]);

  /**
   * A SWEEP BELONGS TO THE AREA THAT PRODUCED IT, so it is discarded the moment
   * that area changes.
   *
   * Resetting only in `onGenerate` left area A's candidates listed, decided and
   * SUBMITTABLE while the panel priced area B and the map showed it — an operator
   * could redraw somewhere else and create stations from the previous box without
   * anything on screen contradicting them. The city is in the dependency list too:
   * it scopes the lane fetch and therefore the corridor band the candidates were
   * judged against.
   *
   * Keyed on the serialised bbox rather than the object, which `useMemo` rebuilds
   * whenever the URL string changes.
   */
  const areaKey = bbox
    ? `${cityId ?? ''}|${bbox.swLng},${bbox.swLat},${bbox.neLng},${bbox.neLat}`
    : `${cityId ?? ''}|`;
  useEffect(() => {
    setCandidates([]);
    setSweptCandidateCount(0);
    setSweptParamsKey(null);
    setSweptLaneCount(null);
    setStats(null);
    setDecisions({});
    setSweepError(null);
    setSubmitResult(null);
  }, [areaKey]);

  /**
   * Every URL write goes through here, so a write can never ACCIDENTALLY drop
   * another param: a redraw keeps the city, and any future writer of one param
   * keeps the rest. Phase 3's bug shape in miniature.
   *
   * "Accidentally" is the whole distinction, and `'bbox' in next` is what draws
   * it. Omitting `bbox` means "leave it alone"; passing `bbox: null` means
   * "clear it, on purpose". `onCityChange` uses the second form deliberately —
   * see there — so DO NOT "fix" this by making the bbox unconditionally sticky.
   */
  const buildSearch = useCallback(
    (next) => {
      const merged = { cityId, ...next };
      const search = new URLSearchParams();
      if (merged.cityId) search.append('cityId', merged.cityId);
      const nextBbox = 'bbox' in next ? next.bbox : bbox;
      if (nextBbox) {
        ['swLng', 'swLat', 'neLng', 'neLat'].forEach((key) => search.append(key, nextBbox[key]));
      }
      return search.toString();
    },
    [cityId, bbox],
  );

  /**
   * CHANGING THE CITY CLEARS THE DRAWN BOX, deliberately — `bbox: null` rather
   * than an omitted `bbox`, so `buildSearch`'s `'bbox' in next` sees the intent.
   *
   * A rectangle in Austin is not a smaller version of a rectangle in Dallas; it
   * is meaningless. Keeping it left the selection off-screen (this handler pans
   * the map to the new city) while the params panel went on quoting a cell count
   * and a dollar cost for it and Generate stayed armed — so switching city and
   * pressing Generate spent real money sweeping an area the operator could not
   * see. Clearing it puts the page back in its "draw a box" state, which is the
   * only honest thing a city change can leave behind.
   */
  const onCityChange = useCallback(
    (city) => {
      history.push({ search: buildSearch({ cityId: city?.id, bbox: null }) });
      setHiddenPairKeys([]);
      if (city?.cityCoord?.coordinates && map) {
        const [lng, lat] = city.cityCoord.coordinates;
        map.panTo({ lat, lng });
        map.setZoom(CITY_ZOOM);
      }
    },
    [history, buildSearch, map],
  );

  // Open on Austin instead of a US-wide view, which is both useless to look at
  // and the state that quotes a continent-sized sweep at six figures.
  //
  // Applied by rewriting the URL rather than by defaulting `selectedCity`,
  // so the URL stays the single source of truth and the link is shareable.
  // `replace`, so Back still leaves the page rather than returning to a
  // city-less view the operator never chose.
  //
  // The ref latches on the first render where cities have loaded, whether or
  // not a city was defaulted. That is what makes "clear the city" a real empty
  // state instead of something that snaps back to Austin — without it, clearing
  // looks identical to arriving fresh.
  const defaultCityApplied = useRef(false);
  useEffect(() => {
    if (defaultCityApplied.current || cities.length === 0) return;
    defaultCityApplied.current = true;
    if (cityId) return;
    const fallback = cities.find((city) => city.cityName === DEFAULT_CITY_NAME);
    if (fallback) history.replace({ search: buildSearch({ cityId: fallback.id }) });
  }, [cities, cityId, history, buildSearch]);

  // `replace`, not `push`: redrawing the box while tuning an area is a
  // refinement of the same view, and a dozen redraws should not mean a dozen
  // presses of Back to leave the page.
  const onBboxChange = useCallback(
    (next) => history.replace({ search: buildSearch({ bbox: next }) }),
    [history, buildSearch],
  );

  // The initial frame. Memoised on the city rather than rebuilt every render:
  // `<GoogleMap>` re-applies `center` whenever the prop's value changes, so a
  // fresh object literal each render would yank the map back and fight the
  // operator's own panning. City changes additionally pan imperatively above,
  // which is what moves the map when it is already mounted.
  const center = useMemo(() => {
    const coordinates = selectedCity?.cityCoord?.coordinates;
    if (!coordinates) return FALLBACK_CENTER;
    const [lng, lat] = coordinates;
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : FALLBACK_CENTER;
  }, [selectedCity]);
  const zoom = selectedCity ? CITY_ZOOM : FALLBACK_ZOOM;

  const {
    data: lanesData,
    error: lanesError,
    isLoading: isLanesLoading,
  } = useTanStackQuery({
    queryKey: ['station-builder-lanes', cityId],
    queryFn: () => laneMapApi.fetchLanes({ cityId }),
    enabled: !!cityId,
    keepPreviousData: true,
  });

  /**
   * Same helper shape LaneMap uses, rather than a second convention for reading
   * a viewport off a map instance.
   */
  const getBoundsFromMap = useCallback((mapInstance) => {
    const b = mapInstance?.getBounds();
    if (!b) return null;
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    return { swLng: sw.lng(), swLat: sw.lat(), neLng: ne.lng(), neLat: ne.lat() };
  }, []);

  // Every settled pan/zoom, not just the first one: the pins are supposed to
  // describe whatever is on screen now. Writing a STRING means an `idle` that
  // did not actually move the viewport sets identical state and re-renders
  // nothing, so it cannot start a fetch.
  const onMapIdle = useCallback(() => {
    if (!map) return;
    const b = getBoundsFromMap(map);
    if (b) setViewportKey(`${b.swLng},${b.swLat},${b.neLng},${b.neLat}`);
  }, [map, getBoundsFromMap]);

  const debouncedViewportKey = useDebouncedValue(viewportKey, VIEWPORT_DEBOUNCE_MS);

  // Rebuilt from the debounced key, so the object handed to the API is always
  // the one the query key names — the two can never describe different boxes.
  const viewportBounds = useMemo(() => {
    if (!debouncedViewportKey) return null;
    const [swLng, swLat, neLng, neLat] = debouncedViewportKey.split(',').map(Number);
    const parsed = { swLng, swLat, neLng, neLat };
    return Object.values(parsed).every((value) => Number.isFinite(value)) ? parsed : null;
  }, [debouncedViewportKey]);

  const {
    data: existingStationsData,
    error: existingStationsError,
  } = useTanStackQuery({
    queryKey: ['station-builder-stations', debouncedViewportKey],
    queryFn: () => laneMapApi.fetchStations(viewportBounds),
    // Hidden means not fetched — react-query still serves the cache when the
    // toggle comes back on, so turning the layer off costs nothing to undo.
    enabled: showExistingStations && !!viewportBounds,
    // Panning should replace the pins when the new set lands, not blank the map
    // and the count in between.
    keepPreviousData: true,
  });

  // Operational ones always; switched-off ones only when asked for. See
  // `EXISTING_STATION_PINS` item 3.
  const existingStations = useMemo(
    () =>
      (existingStationsData?.stations || []).filter(
        (station) => station.isOperational || showNonOperationalStations,
      ),
    [existingStationsData, showNonOperationalStations],
  );

  // Counted off the unfiltered response, not off `existingStations`, so the
  // hint beside the toggle can say how many are being withheld while they are
  // withheld — a count taken after the filter would read 0 in exactly the state
  // where the operator needs the number.
  const nonOperationalInViewCount = useMemo(
    () =>
      (existingStationsData?.stations || []).filter((station) => !station.isOperational).length,
    [existingStationsData],
  );

  const routes = useMemo(() => lanesData?.lanes || [], [lanesData]);
  const pairs = useMemo(() => buildCityPairs(routes), [routes]);
  const visiblePairs = useMemo(
    () => pairs.filter((pair) => !hiddenPairKeys.includes(pair.key)),
    [pairs, hiddenPairKeys],
  );

  const togglePair = useCallback((pairKey) => {
    setHiddenPairKeys((prev) =>
      prev.includes(pairKey) ? prev.filter((key) => key !== pairKey) : [...prev, pairKey],
    );
  }, []);

  /**
   * The corridor band, one buffer per visible operational route.
   *
   * Written here rather than imported: LaneMap's `useRouteBuffers` was
   * deliberately left in place as lane-page-specific. It shares the
   * `['lane-buffer', laneId, radiusMeters]` cache namespace with it anyway, so
   * the two pages serve each other's fetches when the radii coincide.
   *
   * Radius is the SWEEP's corridor radius, debounced: it comes from a number
   * field the operator types in, and each change would otherwise fan out one
   * request per route per keystroke.
   */
  const resolvedParams = toRequestParams(params);
  const corridorRadius = resolvedParams.corridorRadiusMeters;
  const corridorFilterEnabled = !!resolvedParams.corridorFilterEnabled;
  const debouncedCorridorRadius = useDebouncedValue(corridorRadius, 400);
  const corridorRoutes = useMemo(
    () => (showCorridorBand ? visiblePairs.flatMap((pair) => pair.operationalRoutes) : []),
    [showCorridorBand, visiblePairs],
  );
  const corridorEnabled =
    showCorridorBand && !!params.corridorFilterEnabled && !!debouncedCorridorRadius;
  const corridorBuffers = useQueries({
    queries: corridorRoutes.map((route) => ({
      queryKey: ['lane-buffer', route.id, debouncedCorridorRadius],
      queryFn: () =>
        laneMapApi.fetchLaneBuffer({ laneId: route.id, radiusMeters: debouncedCorridorRadius }),
      enabled: corridorEnabled,
      staleTime: Infinity,
    })),
  });

  /**
   * LANE_SCOPE — the lanes the sweep measures against, sent as `laneIds`.
   *
   * The bug this closes: the corridor FILTER ran against every operational
   * route whose polyline came near the swept box, while the corridor BAND was
   * drawn only for the pairs ticked in the sidebar. On a real Austin sweep 24
   * routes crossed the rectangle, 8 were drawn, and the 16 invisible ones were
   * still deciding which candidates survived — so places 2,695 m from
   * `Dallas -> Fredericksburg` looked like junk on a map that never mentioned
   * that lane. The filter was right; the map was incomplete. Sending the
   * selection means the band and the filter are computed from ONE set and
   * cannot disagree again.
   *
   * DERIVED FROM `visiblePairs`, NOT FROM `corridorRoutes`, and that distinction
   * is the whole point. `corridorRoutes` is additionally gated on
   * `showCorridorBand`, a pure display checkbox — sourcing the request from it
   * would mean un-shading the map changed which candidates come back, which is a
   * worse version of the bug above rather than a fix for it. `laneIds` must
   * depend only on WHICH LANES ARE SELECTED.
   */
  const laneIds = useMemo(
    () => visiblePairs.flatMap((pair) => pair.operationalRoutes).map((route) => route.id),
    [visiblePairs],
  );

  /**
   * The params object every request-shaped thing on this page is computed from:
   * the panel's fields plus the derived lane selection.
   *
   * It — not `params` — is what goes to the estimate, to the sweep, and to
   * `requestParamsKey`. `laneIds` changes which candidates come back, so it has
   * to be inside the estimate's cache key (or the price on screen belongs to a
   * different lane set) and inside the drift guard (or unticking a lane after a
   * sweep leaves rows that the settings on screen no longer describe, still
   * submittable). Passing the raw `params` to either would reintroduce exactly
   * the display/request divergence this change exists to remove.
   *
   * `SearchParamsPanel` still receives the raw `params`: it renders and edits
   * those fields, and `laneIds` is not one of them.
   */
  const sweepParams = useMemo(() => ({ ...params, laneIds }), [params, laneIds]);

  /**
   * Why a sweep cannot run right now, or null. Three states that all look like
   * "no lanes selected" to the backend and must not read the same to an operator.
   *
   * The backend 400s on an absent or empty `laneIds` whenever the corridor
   * filter is on, and the estimate fires on a debounce off every param change —
   * so this gates the estimate as well as the button. Otherwise merely opening
   * the page would paint a validation error while the city's lanes were still
   * in flight.
   */
  const laneScopeMessage = useMemo(() => {
    if (!corridorFilterEnabled) return null;
    if (isLanesLoading) return 'Loading this city’s lanes…';
    if (laneIds.length > 0) return null;
    if (pairs.length === 0) {
      return lanesError
        ? 'This city’s lanes could not be loaded, so there is nothing for the corridor filter to measure against. Retry, or turn off the corridor filter to sweep the whole rectangle.'
        : 'This city has no operational lanes, so there is nothing for the corridor filter to measure against. Turn off the corridor filter to sweep the whole rectangle.';
    }
    return 'Select at least one lane under “Lanes to search”. The corridor filter measures candidates against the lanes you pick, so an empty selection has nothing to search.';
  }, [corridorFilterEnabled, isLanesLoading, laneIds.length, pairs.length, lanesError]);

  const { estimate, isLoading: isEstimating, error: estimateError } = useCandidateEstimate(
    { bbox, params: sweepParams },
    { enabled: !laneScopeMessage },
  );
  const { runSweep, isSweeping } = useCandidateSweep();
  const { batchCreateStations } = useBatchCreateStations();
  const { recordRejections } = useRecordRejections();

  /**
   * PARAM_DRIFT — the candidates on screen were produced by particular search
   * settings, and the panel can be edited after they arrive.
   *
   * The failure this closes: draw, Generate, get a list, then raise `minRating`
   * to 4.5. The panel immediately displays and prices the new filters while the
   * list still holds rows produced by the old ones — still approvable, still
   * batch-creatable. The operator creates stations that fail the criteria visible
   * on their own screen. That is the same class of defect as a sweep outliving its
   * bbox, and this page's whole job is to keep the display and the buttons
   * agreeing.
   *
   * WHY THIS IS NOT FIXED BY PUTTING THE PARAMS IN `areaKey`. That would discard
   * the sweep on any param edit, and a sweep costs real money — an Austin-sized
   * one is about $8. The params panel is a dozen number fields; destroying
   * paid-for results because someone typed a digit is a worse failure than the one
   * being fixed. So the data is KEPT and only the writing gate closes.
   *
   * WHY THE BBOX/CITY CASE STILL DISCARDS OUTRIGHT (`areaKey`, above). A different
   * area means the map no longer shows the region the candidates came from, so
   * there is nothing coherent left to display — the pins would sit off-screen and
   * the stats would describe somewhere else. Drifting params, by contrast, leave
   * every candidate exactly where it is and still worth looking at.
   *
   * COMPARED ON THE NORMALISED REQUEST, not on the panel's raw state — see
   * `requestParamsKey`. A blank field that resolves to the server default, a
   * number retyped as an equal string, or the same values in a different key
   * order are NOT drift, because the sweep they would run is the same sweep that
   * was billed. Restoring an edited value therefore clears the warning by itself,
   * which is the property that makes keeping the data worth anything.
   *
   * PER-ROW APPROVE/REJECT STAY ENABLED. Reviewing is not writing: the only
   * side-effecting step is submission, and it is what is blocked. The candidates
   * are unchanged, real, paid-for data, so judgements made about them remain valid
   * — and since the expected remedy is restoring the edited value, work done in
   * the meantime should survive it rather than have to be redone. (Contrast
   * `isSubmitting`, which DOES disable the rows, because there the payload has
   * already been built from `decisions` and a late edit would describe something
   * that is already on the wire.)
   */
  // The `!== null` clause is belt-and-braces rather than load bearing, and is
  // marked as such because it cannot be tested: before a sweep `sweptParamsKey`
  // is null, but `hasSweep` is also false, so the only consumer of this flag —
  // the review list, and the submit handler it owns — is not rendered and not
  // reachable. Dropping the clause changes no observable behaviour today. It
  // stays so that this expression means "the listed sweep is stale" on its own
  // terms, without depending on a second condition several hundred lines away.
  const paramsDrifted = sweptParamsKey !== null && sweptParamsKey !== requestParamsKey(sweepParams);

  const onGenerate = useCallback(async () => {
    // Third gate on the empty lane selection, behind the disabled button and
    // the vetoed estimate: this one spends money, so it refuses rather than
    // relies on the two upstream of it.
    if (laneScopeMessage) return;

    setSweepError(null);
    setSubmitResult(null);
    const result = await runSweep({ bbox, params: sweepParams });
    if (!result.success) {
      setSweepError(result.error);
      return;
    }
    setCandidates(result.candidates);
    setSweptCandidateCount(result.candidates.length);
    // How many lanes were ASKED FOR, kept beside the sweep rather than read live
    // off `laneIds`: the stats block compares it against the backend's
    // `corridorRouteCount` to show how many of them actually had a polyline near
    // the box, and unticking a lane afterwards must not rewrite that comparison
    // for a sweep that has already run.
    setSweptLaneCount(sweepParams.laneIds.length);
    // Recorded from the same params the sweep just ran with, through the same
    // `toRequestParams` the estimate and the sweep share — so what is remembered
    // here cannot disagree with what was actually billed. See `PARAM_DRIFT`.
    setSweptParamsKey(requestParamsKey(sweepParams));
    setStats(result.stats);
    // A new sweep's candidates are a different set; carrying decisions over
    // would silently pre-approve rows an operator never looked at.
    setDecisions({});
  }, [runSweep, bbox, sweepParams, laneScopeMessage]);

  const onDecide = useCallback((placeId, decision) => {
    setDecisions((prev) => {
      const next = { ...prev };
      if (decision === null) delete next[placeId];
      else next[placeId] = decision;
      return next;
    });
  }, []);

  /**
   * The map's one gesture, routed through the SAME `onDecide` the list uses — so
   * there is exactly one writer of `decisions` and no way for the two views to
   * disagree. See `nextCandidateDecision` for why it cycles and what it skips.
   */
  const onCandidatePinClick = useCallback(
    (candidate) => {
      const current = decisions[candidate.placeId] ?? null;
      const next = nextCandidateDecision(current, candidate);
      if (next === current) return;
      onDecide(candidate.placeId, next);
    },
    [decisions, onDecide],
  );

  const onDecideAll = useCallback((decision, placeIds) => {
    setDecisions((prev) => {
      const next = { ...prev };
      placeIds.forEach((placeId) => {
        next[placeId] = decision;
      });
      return next;
    });
  }, []);

  const onSubmit = useCallback(async () => {
    // SECOND GATE, matching the blank-name/address pattern below: the list's
    // button is already disabled while the params have drifted, and this refuses
    // to build a payload from a list the settings on screen no longer describe.
    // One gate keeps the operator from asking; this one keeps it from happening.
    if (paramsDrifted) return;

    setIsSubmitting(true);
    setSubmitResult(null);

    const approvedAll = candidates.filter((candidate) => decisions[candidate.placeId] === APPROVED);
    const rejectedAll = candidates.filter((candidate) => decisions[candidate.placeId] === REJECTED);

    // PRE-FLIGHT. See `SUBMIT_REPORTING`: a blank `name`/`address` is rejected by
    // `ValidationPipe` for the whole chunk, so these rows never go on the wire.
    const approved = approvedAll.filter(canCreate);
    const rejected = rejectedAll.filter(canReject);
    const blocked = [
      ...approvedAll
        .filter((candidate) => !canCreate(candidate))
        .map((candidate) => ({
          placeId: candidate.placeId,
          name: candidate.name,
          what: canReject(candidate) ? 'missing address' : 'missing name',
        })),
      ...rejectedAll
        .filter((candidate) => !canReject(candidate))
        .map((candidate) => ({ placeId: candidate.placeId, name: candidate.name, what: 'missing name' })),
    ];

    let createdCount = 0;
    let failedCount = 0;
    const failures = [];
    const settledPlaceIds = [];

    const approvedChunks = chunk(approved, BATCH_CHUNK_SIZE);
    for (let chunkIndex = 0; chunkIndex < approvedChunks.length; chunkIndex += 1) {
      const group = approvedChunks[chunkIndex];
      const offset = chunkIndex * BATCH_CHUNK_SIZE;
      // eslint-disable-next-line no-await-in-loop
      const result = await batchCreateStations({ stations: group.map(toBatchItem) });

      if (!result.success) {
        // The request itself failed, so NOTHING in this chunk landed. Counting
        // it as neither created nor failed would make the totals not add up.
        failedCount += group.length;
        group.forEach((candidate, index) => {
          failures.push({ row: offset + index, name: candidate.name, error: result.error });
        });
        continue;
      }

      createdCount += result.createdCount;
      failedCount += result.failedCount;
      result.results
        .filter((row) => row.status === 'failed')
        .forEach((row) => {
          failures.push({
            row: offset + row.index,
            name: group[row.index]?.name,
            error: row.error,
          });
        });
      result.results
        .filter((row) => row.status === 'created')
        .forEach((row) => {
          const candidate = group[row.index];
          if (candidate) settledPlaceIds.push(candidate.placeId);
        });
    }

    let recordedCount = 0;
    const rejectionErrors = [];
    const rejectedChunks = chunk(rejected, REJECTION_CHUNK_SIZE);
    for (let chunkIndex = 0; chunkIndex < rejectedChunks.length; chunkIndex += 1) {
      const group = rejectedChunks[chunkIndex];
      // eslint-disable-next-line no-await-in-loop
      const result = await recordRejections({ rejections: group.map(toRejection) });
      if (!result.success) {
        rejectionErrors.push(result.error);
        continue;
      }
      recordedCount += result.recorded;
      group.forEach((candidate) => settledPlaceIds.push(candidate.placeId));
    }

    // Rows that actually landed leave the list, so a retry after a partial
    // failure re-sends only what did not — the alternative is duplicating
    // stations the operator already created.
    if (settledPlaceIds.length > 0) {
      const settled = new Set(settledPlaceIds);
      setCandidates((prev) => prev.filter((candidate) => !settled.has(candidate.placeId)));
      setDecisions((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([placeId]) => !settled.has(placeId))),
      );
    }

    setSubmitResult({
      createdCount,
      failedCount,
      failures,
      recordedCount,
      rejectionErrors,
      blocked,
      message: summarise({
        createdCount,
        failedCount,
        failures,
        recordedCount,
        rejectionErrors,
        blocked,
      }),
    });
    setIsSubmitting(false);
  }, [candidates, decisions, batchCreateStations, recordRejections, paramsDrifted]);

  const hasSweep = candidates.length > 0 || !!stats;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={cardHeaderSx}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em', flex: 1 }}>
            Station Builder
          </Typography>
          {/*
            The way back. `/stations` is a sibling route under the same shell, so
            this is a plain push rather than a browser Back: an operator who
            arrived by typing the URL, or who has redrawn the box a few times
            (each a `replace`), has no single Back that lands on the list.
          */}
          <Button onClick={() => history.push('/stations')} sx={pillButtonSx}>
            Back to list
          </Button>
          <FilterSelect
            icon={<StationIcon />}
            label="City"
            placeholder="Pick a city"
            clearLabel="No city"
            options={cities}
            value={selectedCity}
            onChange={onCityChange}
            // Joined through a filter, not interpolated: `stateAbbr` is nullable
            // on the city row, and a template literal renders "Austin, undefined"
            // in the pill for one.
            getOptionLabel={(city) => [city.cityName, city.stateAbbr].filter(Boolean).join(', ')}
          />
        </Stack>
      </Box>

      {!cityId ? (
        <Alert severity="info" sx={{ mx: 4, mt: 1 }}>
          Pick a city to frame the map and load its lanes. The city itself is never sent: the
          sweep is scoped by the rectangle you place and by the lanes you tick, and candidates
          are filed to a city from their own coordinates.
        </Alert>
      ) : null}

      {lanesError ? (
        // Said out loud, because "no lanes drawn" and "the lane fetch failed" look
        // identical otherwise: the sidebar list and the corridor band both just
        // vanish, and blank space would read as "this city has nothing to
        // search" — a different problem with a different remedy.
        <Alert severity="error" sx={{ mx: 4, mt: 1 }}>
          {`Could not load this city's lanes: ${lanesError.message ?? 'unknown error'}. The lane list and the corridor band are missing, not empty — and since the sweep searches the lanes you select, there is nothing to select and no sweep to run until this loads.`}
        </Alert>
      ) : null}

      {existingStationsError ? (
        // Same reasoning as the lane error above, and the stakes are higher: an
        // operator who reads a failed fetch as "nothing here" sites a duplicate,
        // and the sweep then drops their candidate as `nearExistingStation` with
        // nothing on screen explaining why.
        <Alert severity="error" sx={{ mx: 4, mt: 1 }}>
          {`Could not load the stations already in this area: ${existingStationsError.message ?? 'unknown error'}. The map is missing them, not showing that there are none — a candidate too close to one is still dropped as nearExistingStation.`}
        </Alert>
      ) : null}

      {existingStationsData?.truncated ? (
        // The endpoint fetches cap+1 and reports the overflow rather than
        // silently capping, so blank space in a dense metro can mean "capped",
        // not "empty" — which is the same duplicate-siting mistake as an outage.
        <Alert severity="warning" sx={{ mx: 4, mt: 1 }}>
          Too many stations in this viewport to show them all, so some are missing from the map.
          Zoom in for a complete picture before siting anything here.
        </Alert>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <Box
          sx={{
            width: 380,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
          }}
        >
          {pairs.length > 0 ? (
            <Box sx={{ p: 2 }}>
              {/*
                NOT a map legend, and the heading has to say so. These
                checkboxes used to toggle display only, which is how the band
                came to show 8 lanes while the sweep silently measured against
                24 — see `LANE_SCOPE`. They now pick the lanes the sweep
                searches, so the section is named for what it does and the
                caption states both effects; "Corridor band" below is the one
                control here that is still purely visual, and it is labelled
                as the display toggle it is.
              */}
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {`Lanes to search (${pairs.length} city pairs)`}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {`Ticking a pair both shades its corridor on the map and includes its lanes in the sweep: candidates are kept only if they fall within ${corridorRadius} m of a lane you have selected, and dropped as outsideCorridor otherwise.`}
              </Typography>
              <FormControlLabel
                label={
                  <Typography variant="body2">Show the shaded band (display only)</Typography>
                }
                control={
                  <Checkbox
                    name="show-corridor-band"
                    size="small"
                    checked={showCorridorBand}
                    onChange={() => setShowCorridorBand((prev) => !prev)}
                  />
                }
              />
              {laneScopeMessage ? (
                <Alert severity="warning" sx={{ my: 1 }}>
                  {laneScopeMessage}
                </Alert>
              ) : null}
              {pairs.map((pair) => (
                <FormControlLabel
                  key={pair.key}
                  label={
                    // The lane colour goes on a swatch, not on the text. This
                    // palette is picked to stay legible as a stroke over a light
                    // basemap, which makes several of its entries (the dark
                    // blues and purples especially) close to unreadable as body
                    // text on this dark panel. The swatch still ties the row to
                    // its polyline, and the label keeps normal contrast.
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 4,
                          borderRadius: 0.5,
                          flexShrink: 0,
                          backgroundColor: laneColorFor(pair.key),
                        }}
                      />
                      <Typography variant="body2">{pair.label}</Typography>
                    </Stack>
                  }
                  control={
                    <Checkbox
                      name={`pair:${pair.key}`}
                      size="small"
                      checked={!hiddenPairKeys.includes(pair.key)}
                      onChange={() => togglePair(pair.key)}
                    />
                  }
                  // NOT `display: block`, which is what broke this onto two
                  // lines: FormControlLabel is a flex row, and overriding it
                  // stacks the checkbox above its own label. Full width plus no
                  // margin gives one row per pair while keeping them inline.
                  sx={{ width: '100%', m: 0 }}
                />
              ))}
            </Box>
          ) : null}

          {/*
            OUTSIDE the lane block above, deliberately. Existing stations have
            nothing to do with lanes, and nesting this control there would make
            it disappear for a city whose lane fetch returned nothing — taking
            the only way to clear a cluttered metro with it.
          */}
          <Box sx={{ px: 2, pb: 2, pt: pairs.length > 0 ? 0 : 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Existing stations
            </Typography>
            <FormControlLabel
              label={
                <Typography variant="body2">
                  {/*
                    One JS string, not `{n} in view`: MUI/JSX interpolation
                    splits that into separate children and a text joiner then
                    reads a double space between the number and the prose.
                  */}
                  {`Show pins (${existingStations.length} in view)`}
                </Typography>
              }
              control={
                <Checkbox
                  name="show-existing-stations"
                  size="small"
                  checked={showExistingStations}
                  onChange={() => setShowExistingStations((prev) => !prev)}
                />
              }
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Stations in the current map view, so you can see what is already covered. They
              follow the map, not the selected city — the sweep drops a candidate as
              nearExistingStation on distance alone, including from a station in the next city
              over.
            </Typography>

            {/*
              Nested under the pins toggle and disabled with it, rather than a
              sibling: with pins off it can change nothing on screen, and a live
              checkbox that does nothing is worse than a greyed one that explains
              itself. State is left alone when it is disabled, so the choice
              survives clearing the pins and bringing them back.
            */}
            <FormControlLabel
              disabled={!showExistingStations}
              sx={{ ml: 2, mt: 0.5 }}
              label={
                <Typography variant="body2">
                  {/* One JS string, for the reason given on the toggle above. */}
                  {`Include non-operational (${nonOperationalInViewCount} in view)`}
                </Typography>
              }
              control={
                <Checkbox
                  name="show-non-operational-stations"
                  size="small"
                  checked={showNonOperationalStations}
                  onChange={() => setShowNonOperationalStations((prev) => !prev)}
                />
              }
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
              Drawn hollow. Non-operational stations are switched off for riders — they never
              appear in quote selection — so they are not coverage. Show them to find and manage
              stations that are otherwise invisible on this map.
            </Typography>
          </Box>

          {/*
            ALSO OUTSIDE the lane block, and for the same reason the existing
            station toggle is: a city whose lane fetch returned nothing would
            otherwise get candidate pins with no way to hide them.

            Gated on `candidates.length` — not on `stats`, and not unconditional.
            Pins exist exactly when there are candidates, so this control is
            present in every state that can draw one, and a sweep that kept
            nothing does not leave a "(0)" checkbox behind that does nothing.
          */}
          {candidates.length > 0 ? (
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Sweep candidates
              </Typography>
              <FormControlLabel
                label={
                  <Typography variant="body2">
                    {/* One JS string, not `{n} on the map` — see the note above. */}
                    {`Show pins (${candidates.length} on the map)`}
                  </Typography>
                }
                control={
                  <Checkbox
                    name="show-candidate-pins"
                    size="small"
                    checked={showCandidatePins}
                    onChange={() => setShowCandidatePins((prev) => !prev)}
                  />
                }
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Diamonds are places this sweep proposed; the dark dots are stations that already
                exist. Amber is undecided, green approved, red faded rejected. Clicking a diamond
                cycles it undecided → approved → rejected, the same decision the list records.
              </Typography>
            </Box>
          ) : null}

          <Divider />

          <SearchParamsPanel
            params={params}
            onChange={setParams}
            onReset={() => setParams(DEFAULT_SEARCH_PARAMS)}
            estimate={estimate}
            isEstimating={isEstimating}
            estimateError={estimateError}
            onGenerate={onGenerate}
            isGenerating={isSweeping}
            hasArea={!!bbox}
            laneScopeMessage={laneScopeMessage}
          />

          {sweepError ? (
            <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
              {sweepError}
            </Alert>
          ) : null}

          {submitResult ? (
            <Alert
              severity={
                submitResult.failedCount > 0 ||
                submitResult.rejectionErrors.length > 0 ||
                submitResult.blocked.length > 0
                  ? 'error'
                  : 'success'
              }
              sx={{ mx: 2, mb: 2 }}
            >
              <Typography variant="body2">{submitResult.message}</Typography>
              {submitResult.failures.map((failure) => (
                <Typography variant="caption" display="block" key={`${failure.row}:${failure.name}`}>
                  {`Row ${failure.row + 1} ${failure.name ?? '(unnamed)'}: ${failure.error ?? 'unknown error'}`}
                </Typography>
              ))}
              {submitResult.blocked.map((row) => (
                <Typography variant="caption" display="block" key={`blocked-${row.placeId}`}>
                  {`Not sent — ${row.what}: ${row.name || '(unnamed place)'}. The batch DTO requires both, and one blank field 400s the whole chunk, so this row was excluded rather than allowed to take the others down with it.`}
                </Typography>
              ))}
              {submitResult.rejectionErrors.map((error, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography variant="caption" display="block" key={`rejection-error-${index}`}>
                  {`Rejections not recorded: ${error ?? 'unknown error'}. The next sweep over this area will propose them again.`}
                </Typography>
              ))}
            </Alert>
          ) : null}

          {hasSweep ? (
            <>
              <Divider />
              <CandidateReviewList
                candidates={candidates}
                stats={stats}
                keptCandidateCount={sweptCandidateCount}
                decisions={decisions}
                onDecide={onDecide}
                onDecideAll={onDecideAll}
                onSubmit={onSubmit}
                isSubmitting={isSubmitting}
                paramsDrifted={paramsDrifted}
                requestedLaneCount={sweptLaneCount}
              />
            </>
          ) : null}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <Map
            center={center}
            zoom={zoom}
            onLoad={setMap}
            onIdle={onMapIdle}
            mapContainerStyle={{ width: '100%', height: '100%' }}
          >
            {corridorEnabled
              ? corridorBuffers.map((query, index) => {
                  const geojson = query.data?.geojson;
                  if (!geojson) return null;
                  return (
                    <Polygon
                      key={`corridor-${corridorRoutes[index].id}`}
                      paths={geoJsonToPolygonPaths(geojson)}
                      options={{
                        fillColor: '#4285F4',
                        fillOpacity: 0.1,
                        strokeColor: '#4285F4',
                        strokeOpacity: 0.6,
                        strokeWeight: 1,
                        clickable: false,
                      }}
                    />
                  );
                })
              : null}

            {visiblePairs.map((pair) =>
              pair.routes.map((route) => (
                <Polyline
                  key={route.id}
                  path={geoJsonToPath(route.polylineGeoJson, route.id)}
                  options={{
                    strokeColor: laneColorFor(pair.key),
                    strokeOpacity: 0.9,
                    strokeWeight: 3,
                  }}
                />
              )),
            )}

            {/*
              Before `AreaSelect`, so the editable rectangle and its drag handles
              sit above the pins rather than under them.

              FLAT `latitude`/`longitude`, straight through with no remapping —
              this endpoint already returns scalars. See the field-name trap in
              `EXISTING_STATION_PINS`.
            */}
            {showExistingStations
              ? existingStations.map((station) => (
                  <Marker
                    key={station.id}
                    position={{ lat: station.latitude, lng: station.longitude }}
                    icon={existingStationIcon(station)}
                    title={existingStationTooltip(station)}
                  />
                ))
              : null}

            {/*
              AFTER the station pins, so a proposal sits above the thing it might
              duplicate rather than under it — the overlap is the interesting case.

              Keyed on `placeId`: a candidate has no station `id`, and `placeId`
              is what `decisions` is keyed on and what both DTOs identify a row
              by. Flat `latitude`/`longitude` straight through, as with stations —
              see `CANDIDATE_PINS` item 3 for why the two shapes still must not be
              treated as one.
            */}
            {showCandidatePins
              ? candidates.map((candidate) => {
                  const decision = decisions[candidate.placeId] ?? null;
                  return (
                    <Marker
                      key={`candidate-${candidate.placeId}`}
                      position={{ lat: candidate.latitude, lng: candidate.longitude }}
                      icon={candidateIcon(decision)}
                      title={candidateTooltip(candidate, decision)}
                      // No decisions mid-submit, matching the list's disabled
                      // buttons: the payload has already been built from this map.
                      clickable={!isSubmitting}
                      onClick={isSubmitting ? undefined : () => onCandidatePinClick(candidate)}
                    />
                  );
                })
              : null}

            <AreaSelect
              bbox={bbox}
              onChange={onBboxChange}
              // The same instance `onLoad` already captured for the city pan.
              // `AreaSelect` cuts its seed rectangle from this map's viewport;
              // it could read context instead, but one source of the instance on
              // this page is worth more than one fewer prop.
              map={map}
              cells={estimate?.cells ?? null}
              disabled={isSweeping || isSubmitting}
            />
          </Map>
        </Box>
      </Box>
    </Box>
  );
};

export default StationBuilder;
